<template>
  <div class="project-description">
    <div class="body">
      <!-- 1. Saisie du projet -->
      <template v-if="etape === 'saisie'">
        <div class="head">
          <svg
            class="head-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            aria-hidden="true"
          >
            <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-4-4L4 16v4Z" />
            <path d="m13.5 6.5 4 4" />
          </svg>
          <h2>Ou écrivez votre projet</h2>
          <span class="new">Nouveau</span>
        </div>
        <p class="sub">
          Décrivez-le comme à un conseiller. Nous remplissons le simulateur pour vous, vous vérifiez avant le
          calcul.
        </p>
        <label
          for="project-text"
          class="visually-hidden"
        >
          Mon projet
        </label>
        <textarea
          id="project-text"
          v-model="texte"
          class="text"
          maxlength="1500"
          placeholder="Ex. : T2 de 42 m² à Lille, 165 000 €, loué 750 € par mois. 25 000 € d'apport, je gagne 3 200 € net par mois. LMNP ou location nue ?"
        />
        <div class="chips">
          <button
            v-for="exemple in EXEMPLES"
            :key="exemple.libelle"
            type="button"
            class="chip"
            @click="texte = exemple.texte"
          >
            {{ exemple.libelle }}
          </button>
        </div>
        <div class="actions">
          <uikit-button
            type="primary"
            :inactive="texte.trim().length < 10 || chargement"
            :loading="chargement"
            @click="analyser(texte)"
          >
            Calculer mon projet
          </uikit-button>
        </div>
        <p class="ai">
          Assistant IA : il remplit le simulateur à partir de votre texte et peut se tromper. Votre texte est
          lu par un modèle d'IA hébergé par Amazon Web Services en Irlande, puis effacé : nous n'en gardons
          que la longueur.
          <a
            :href="LIEN_CONFIDENTIALITE"
            target="_blank"
            rel="noopener"
          >
            Confidentialité
          </a>
        </p>
        <p
          v-if="message"
          class="message"
          role="status"
        >
          {{ message }}
        </p>
      </template>

      <!-- 2. Une précision (ville, arrondissement, homonymes, prix) -->
      <template v-else-if="etape === 'question'">
        <div class="head">
          <h2>Une précision avant le calcul</h2>
        </div>
        <p class="ask">
          {{ question.texte }}
        </p>
        <div
          v-if="question.options.length"
          class="chips"
        >
          <button
            v-for="option in question.options"
            :key="option.libelle"
            type="button"
            class="chip"
            @click="choisir(option)"
          >
            {{ option.libelle }}
          </button>
        </div>
        <form
          v-else
          class="answer"
          @submit.prevent="repondre"
        >
          <label
            for="project-answer"
            class="visually-hidden"
          >
            {{ question.texte }}
          </label>
          <input
            id="project-answer"
            v-model="reponse"
            class="input"
            :inputmode="question.code === 'prix' ? 'numeric' : 'text'"
            :placeholder="question.code === 'prix' ? 'Prix en euros' : 'Commune'"
          />
          <uikit-button
            type="primary"
            :inactive="!reponse.trim() || chargement"
            :loading="chargement"
            @click="repondre"
          >
            Valider
          </uikit-button>
        </form>
        <button
          type="button"
          class="link"
          @click="recommencer"
        >
          Modifier mon texte
        </button>
      </template>

      <!-- 3. Ce que nous avons compris -->
      <template v-else-if="etape === 'verification'">
        <div class="head">
          <svg
            class="head-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            aria-hidden="true"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          <h2>Ce que nous avons compris</h2>
        </div>
        <p class="quote">« {{ texteAnalyse }} »</p>
        <div class="legend">
          <span>
            <i class="dot texte" />
            dans votre texte
          </span>
          <span>
            <i class="dot hypothese" />
            hypothèse Horiz, à vérifier
          </span>
          <span>
            <i class="dot vous" />
            précisé par vous
          </span>
        </div>
        <div class="rows">
          <div
            v-for="champ in champs"
            :key="champ.cle"
            class="row"
          >
            <span class="label">{{ champ.libelle }}</span>
            <span class="value">
              <i
                class="dot"
                :class="champ.source"
              />
              <input
                v-if="enEdition === champ.cle"
                :id="`project-edit-${champ.cle}`"
                v-model="valeurEditee"
                class="input small"
                inputmode="numeric"
                :aria-label="champ.libelle"
                @keydown.enter.prevent="validerEdition(champ)"
                @keydown.esc="enEdition = null"
                @blur="validerEdition(champ)"
              />
              <span v-else>{{ champ.valeur }}</span>
            </span>
            <button
              v-if="CHAMPS_MODIFIABLES[champ.cle] && enEdition !== champ.cle"
              type="button"
              class="link small"
              @click="editer(champ)"
            >
              Modifier
            </button>
          </div>
        </div>
        <p
          v-if="nonRetenus.length"
          class="ai"
        >
          Non retenu faute de chiffre clair dans votre texte : {{ nonRetenus.join(', ') }}. Vous pourrez le
          préciser dans le rapport.
        </p>
        <div class="actions">
          <uikit-button
            type="primary"
            @click="voirSimulation"
          >
            Voir ma simulation
          </uikit-button>
          <uikit-button
            type="secondary"
            @click="recommencer"
          >
            Modifier mon texte
          </uikit-button>
        </div>
      </template>
    </div>

    <div class="specialist">
      <p>
        <b>Un spécialiste Horiz</b>
        étudie votre projet avec vous, gratuitement. Rappel sous 24 h ouvrées.
      </p>
      <uikit-button
        type="primary"
        @click="ouvrirRappel"
      >
        Être rappelé
      </uikit-button>
    </div>

    <!-- Fenêtre « Être rappelé » : sur la même page, pas de <transition> (calque invisible
         laissé derrière, cf. page de vente du simulateur). -->
    <Teleport to="body">
      <div
        v-if="rappel.ouvert"
        class="callback-overlay"
        @click.self="rappel.ouvert = false"
      >
        <div
          class="callback-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="callback-title"
        >
          <template v-if="!rappel.envoye">
            <div class="callback-head">
              <div>
                <p class="ai">Gratuit et sans engagement</p>
                <h2 id="callback-title">Être rappelé par un spécialiste Horiz</h2>
              </div>
              <button
                type="button"
                class="close"
                aria-label="Fermer"
                @click="rappel.ouvert = false"
              >
                ×
              </button>
            </div>
            <p class="sub">
              Un spécialiste vous appelle sous 24 h ouvrées : recherche du bien, financement, fiscalité,
              gestion.
            </p>
            <form
              class="callback-form"
              novalidate
              @submit.prevent="envoyerRappel"
            >
              <div class="grid">
                <div>
                  <label for="callback-prenom">Prénom</label>
                  <input
                    id="callback-prenom"
                    v-model="rappel.prenom"
                    class="input"
                    autocomplete="given-name"
                  />
                  <small
                    v-if="rappel.erreurs.prenom"
                    class="error"
                  >
                    {{ rappel.erreurs.prenom }}
                  </small>
                </div>
                <div>
                  <label for="callback-tel">Téléphone</label>
                  <input
                    id="callback-tel"
                    v-model="rappel.telephone"
                    class="input"
                    type="tel"
                    autocomplete="tel"
                    placeholder="06 12 34 56 78"
                  />
                  <small
                    v-if="rappel.erreurs.telephone"
                    class="error"
                  >
                    {{ rappel.erreurs.telephone }}
                  </small>
                </div>
                <div>
                  <label for="callback-email">
                    E-mail
                    <span class="optional">(facultatif)</span>
                  </label>
                  <input
                    id="callback-email"
                    v-model="rappel.email"
                    class="input"
                    type="email"
                    autocomplete="email"
                  />
                </div>
                <div>
                  <label for="callback-moment">Quand vous appeler ?</label>
                  <select
                    id="callback-moment"
                    v-model="rappel.moment"
                    class="input"
                  >
                    <option
                      v-for="moment in MOMENTS"
                      :key="moment"
                    >
                      {{ moment }}
                    </option>
                  </select>
                </div>
              </div>
              <fieldset class="needs">
                <legend>
                  Sur quoi souhaitez-vous être aidé ?
                  <span class="optional">(facultatif)</span>
                </legend>
                <label
                  v-for="besoin in BESOINS"
                  :key="besoin"
                >
                  <input
                    v-model="rappel.besoins"
                    type="checkbox"
                    :value="besoin"
                  />
                  {{ besoin }}
                </label>
              </fieldset>
              <div>
                <label for="callback-projet">
                  Votre projet
                  <span class="optional">(joint à la demande, modifiable)</span>
                </label>
                <textarea
                  id="callback-projet"
                  v-model="rappel.projet"
                  class="input"
                  rows="3"
                />
              </div>
              <label
                class="consent"
                for="callback-accord"
              >
                <input
                  id="callback-accord"
                  v-model="rappel.accord"
                  type="checkbox"
                />
                <span>{{ TEXTE_ACCORD }}</span>
              </label>
              <small
                v-if="rappel.erreurs.accord"
                class="error"
              >
                {{ rappel.erreurs.accord }}
              </small>
              <p class="ai">
                Vos coordonnées et votre projet servent uniquement à vous rappeler. Ils sont conservés 3 ans
                au plus après notre dernier échange.
                <a
                  :href="LIEN_CONFIDENTIALITE"
                  target="_blank"
                  rel="noopener"
                >
                  Confidentialité
                </a>
              </p>
              <input
                v-model="rappel.hp"
                class="hp"
                tabindex="-1"
                autocomplete="off"
                aria-hidden="true"
              />
              <div class="actions">
                <uikit-button
                  type="primary"
                  @click="envoyerRappel"
                >
                  Être rappelé
                </uikit-button>
              </div>
            </form>
          </template>
          <template v-else>
            <div class="callback-head">
              <h2 id="callback-title">Demande envoyée</h2>
              <button
                type="button"
                class="close"
                aria-label="Fermer"
                @click="rappel.ouvert = false"
              >
                ×
              </button>
            </div>
            <p class="sub">
              Merci {{ rappel.prenom }}. Un spécialiste Horiz vous appelle sous 24 h ouvrées au
              {{ rappel.telephone }}, {{ rappel.moment.toLowerCase().replace(/ \(.*\)/, '') }}. Votre projet
              est joint à la demande.
            </p>
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { UikitButton } from '@hz/ui-kit'
import { hooksFetchPost } from '../../core/api'

// « Écrire mon projet » à côté du simulateur. Le serveur (hooks, route
// /v1/simulation-description) range le texte dans les champs du simulateur et vérifie
// chaque chiffre ; cette carte affiche ce qu'il a compris, laisse corriger, puis ouvre le
// rapport habituel. Visible pour tous sur /rendement-locatif depuis le 27/10/2026.
// Code d'accès attendu par le serveur (hooks, réglage SIMULATION_DESCRIPTION_CODES_ESSAI).
// Né du lien de test, il est désormais envoyé par la page publique. Pour fermer la carte
// à tous sans redéployer le site : changer ce réglage côté serveur, ou couper le service
// (SIMULATION_DESCRIPTION_ENABLED=false) ; la carte affiche alors le message fixe.
const CODE_ACCES = 'w0bmkugd4h'
const config = useRuntimeConfig()
// Politique de confidentialité (page du site historique).
const LIEN_CONFIDENTIALITE = 'https://horiz.io/rendement-locatif/conditions-generales/confidentialite'

const EXEMPLES = [
  {
    libelle: 'T2 à Lille, LMNP ou nu ?',
    texte:
      "Je veux acheter un T2 de 42 m² à Lille pour 165 000 €, loué 750 € par mois. J'ai 25 000 € d'apport, je gagne 3 200 € net par mois, célibataire. LMNP ou location nue ?",
  },
  {
    libelle: 'Studio à Lyon, passer en LMNP ?',
    texte:
      "J'ai acheté un studio de 22 m² à Lyon en 2019 pour 118 000 €, payé comptant. Il est loué 590 € par mois, je suis au micro-foncier et imposé à 30 %. Est-ce que je gagnerais à passer en LMNP ?",
  },
  {
    libelle: 'Maison à rénover à Rennes',
    texte: 'Maison à rénover à Rennes, 150 000 € avec 50 000 € de travaux, loyer prévu 1 100 € par mois.',
  },
]
const MESSAGE_INDISPONIBLE =
  'La lecture de votre texte est momentanément indisponible. Utilisez le formulaire du simulateur, il donne le même calcul.'
// Seuls les montants qui correspondent un pour un à un paramètre du simulateur se corrigent
// ici ; le reste se modifie dans le rapport.
const CHAMPS_MODIFIABLES = {
  prix: 'Prix',
  loyer: 'Loyer',
  apport: 'Apport',
  revenus: 'Revenus du foyer',
  travauxr: 'Travaux',
  tf: 'Taxe foncière',
  mbles: 'Mobilier',
}
const SUFFIXES = { loyer: ' / mois', revenus: ' / an', tf: ' / an' }

const texte = ref('')
const texteAnalyse = ref('')
const chargement = ref(false)
const message = ref('')
const etape = ref('saisie')
const parametres = ref({})
const champs = ref([])
const nonRetenus = ref([])
const questions = ref([])
const reponse = ref('')
const enEdition = ref(null)
const valeurEditee = ref('')

const question = computed(() => questions.value[0])

const euros = (n) => `${Math.round(n).toLocaleString('fr-FR')} €`
const versNombre = (s) => {
  const n = Number(
    String(s)
      .replace(/[\s\u00a0\u202f€]/g, '')
      .replace(',', '.'),
  )

  return Number.isFinite(n) && n >= 0 ? n : null
}

async function analyser(contenu) {
  if (chargement.value || contenu.trim().length < 10) return
  chargement.value = true
  message.value = ''
  try {
    const resultat = await hooksFetchPost('/v1/simulation-description', {
      body: { texte: contenu, essai: CODE_ACCES },
    })
    texteAnalyse.value = contenu
    if (['hors_sujet', 'indisponible', 'limite'].includes(resultat.statut)) {
      message.value = resultat.message || MESSAGE_INDISPONIBLE
      etape.value = 'saisie'

      return
    }
    parametres.value = { ...resultat.parametres }
    champs.value = resultat.champs.map((champ) => ({ ...champ }))
    nonRetenus.value = resultat.nonRetenus || []
    questions.value = resultat.questions || []
    etape.value = questions.value.length ? 'question' : 'verification'
  } catch (erreur) {
    // Panne, délai, ou route refermée côté serveur (404) : message fixe, le formulaire à
    // côté donne le même calcul.
    message.value = MESSAGE_INDISPONIBLE
    etape.value = 'saisie'
  } finally {
    chargement.value = false
  }
}

function questionSuivante() {
  questions.value = questions.value.slice(1)
  reponse.value = ''
  etape.value = questions.value.length ? 'question' : 'verification'
}

function choisir(option) {
  Object.assign(parametres.value, option.patch)
  if (option.patch.ville) {
    const ligne = { cle: 'ville', libelle: 'Ville', valeur: option.libelle, source: 'vous' }
    const index = champs.value.findIndex((champ) => champ.cle === 'ville')
    if (index >= 0) champs.value.splice(index, 1, ligne)
    else champs.value.splice(1, 0, ligne)
  }
  questionSuivante()
}

function repondre() {
  if (!reponse.value.trim()) return
  if (question.value.code === 'prix') {
    const prix = versNombre(reponse.value)
    if (!prix || prix < 10000) return
    parametres.value.prix = prix
    champs.value.splice(1, 0, { cle: 'prix', libelle: 'Prix', valeur: euros(prix), source: 'vous' })
    questionSuivante()

    return
  }
  // Commune : on relit le texte complété, pour que le serveur vérifie la commune.
  analyser(`${texteAnalyse.value} Le bien se trouve à ${reponse.value.trim()}.`)
}

function editer(champ) {
  enEdition.value = champ.cle
  valeurEditee.value = parametres.value[champ.cle] ?? ''
  nextTick(() => document.getElementById(`project-edit-${champ.cle}`)?.focus())
}

function validerEdition(champ) {
  if (enEdition.value !== champ.cle) return
  const valeur = versNombre(valeurEditee.value)
  enEdition.value = null
  if (valeur === null) return
  parametres.value[champ.cle] = valeur
  if (champ.cle === 'loyer') parametres.value.loyerannuel = Math.round(valeur * 12)
  champ.valeur = `${euros(valeur)}${SUFFIXES[champ.cle] || ''}`
  champ.source = 'vous'
}

// Même format que l'extension navigateur : « cle_valeur » séparés par des virgules.
const lienRapport = computed(() => {
  const computeParameters = Object.entries(parametres.value)
    .filter(([, valeur]) => valeur !== undefined && valeur !== null && valeur !== '')
    .map(([cle, valeur]) => `${encodeURIComponent(cle)}_${encodeURIComponent(String(valeur))}`)
    .join(',')

  return `${config.public.appBaseUrl}/dashboard/future-investor?computeParameters=${computeParameters}`
})

function voirSimulation() {
  window.location.href = lienRapport.value
}

function recommencer() {
  etape.value = 'saisie'
  questions.value = []
  enEdition.value = null
}

// --- Rappel par un spécialiste --------------------------------------------------------
// Même script Google que les formulaires de la page de vente et de l'app (Google Sheet
// « Leads Horiz »). Envoi direct navigateur en no-cors : horiz.io n'accepte pas de POST
// côté serveur (CloudFront).
const LEAD_WEBHOOK_URL =
  'https://script.google.com/macros/s/AKfycbyLCwV4T51tkgxF6Q3ge2tJMb_N5qdxdtd3Fd9V7A8tCrLwVNi4HIhX7mRcsGnpn1j4/exec'
const TEXTE_ACCORD = "J'accepte d'être rappelé par Horiz au sujet de ce projet."
const MOMENTS = [
  'Le matin (9 h - 12 h)',
  'Le midi (12 h - 14 h)',
  "L'après-midi (14 h - 18 h)",
  'En fin de journée (18 h - 20 h)',
]
const BESOINS = [
  'Trouver le bien',
  "Financer l'achat",
  'Choisir le régime fiscal',
  'Comptabilité',
  'Gérer la location',
]

const rappel = reactive({
  ouvert: false,
  envoye: false,
  prenom: '',
  telephone: '',
  email: '',
  moment: MOMENTS[0],
  besoins: [],
  projet: '',
  accord: false,
  hp: '',
  erreurs: { prenom: '', telephone: '', accord: '' },
})

function ouvrirRappel() {
  rappel.ouvert = true
  rappel.envoye = false
  if (!rappel.projet) rappel.projet = texteAnalyse.value || texte.value
}

function envoyerRappel() {
  const telephone = rappel.telephone.replace(/[\s.-]/g, '')
  rappel.erreurs.prenom = rappel.prenom.trim() ? '' : 'Indiquez votre prénom.'
  rappel.erreurs.telephone = /^(0[1-9]\d{8}|\+33[1-9]\d{8})$/.test(telephone)
    ? ''
    : 'Indiquez un numéro à 10 chiffres, par exemple 06 12 34 56 78.'
  rappel.erreurs.accord = rappel.accord ? '' : 'Cochez cette case pour que nous puissions vous appeler.'
  if (rappel.erreurs.prenom || rappel.erreurs.telephone || rappel.erreurs.accord) return

  if (!rappel.hp) {
    try {
      fetch(LEAD_WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          firstName: rappel.prenom.trim(),
          lastName: '',
          email: rappel.email.trim(),
          phone: rappel.telephone.trim(),
          consent: true,
          source: 'Écrire mon projet - Être rappelé',
          page: 'rendement-locatif-essai',
          city: String(parametres.value.ville || '').replace('|', ' '),
          price: parametres.value.prix || '',
          projet: rappel.projet.trim(),
          moment: rappel.moment,
          besoins: rappel.besoins.join(', '),
          simulation: etape.value === 'verification' ? lienRapport.value : '',
          // Preuve de l'accord préalable à l'appel (obligatoire depuis le 11/08/2026).
          accordRappel: `${TEXTE_ACCORD} ${new Date().toISOString()} horiz.io/rendement-locatif`,
        }),
      }).catch(() => {})
    } catch (e) {
      // Envoi au mieux : ne jamais bloquer la personne.
    }
  }
  rappel.envoye = true
}
</script>

<style lang="less" scoped>
.project-description {
  width: 100%;
  background-color: var(--ds-color-white-100);
  border: 2px solid #f4f7fb;
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.04) 0 2px 10px 0;
  display: flex;
  flex-direction: column;
  text-align: left;
}

.body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.head {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--ds-color-primary-100);

  h2 {
    font-size: 18px;
    font-weight: var(--ds-weight-semi-bold);
    margin: 0;
  }
}

.head-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.new {
  font-size: 11px;
  font-weight: var(--ds-weight-semi-bold);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: var(--ds-color-primary-50);
  border-radius: 4px;
  padding: 2px 6px;
}

.sub,
.ask {
  font-size: var(--ds-font-sm);
  color: var(--ds-color-gray-50);
  margin: 0;
}

.ask {
  font-size: var(--ds-font-md);
  font-weight: var(--ds-weight-semi-bold);
  color: var(--ds-color-gray-100);
}

.text,
.input {
  width: 100%;
  border: 1px solid #dcdfe4;
  border-radius: 8px;
  padding: 11px 12px;
  font: inherit;
  font-size: var(--ds-font-sm);
  color: var(--ds-color-gray-100);
  background: var(--ds-color-white-100);

  &:focus {
    outline: none;
    border-color: var(--ds-color-primary-100);
  }
}

.text {
  min-height: 112px;
  resize: vertical;
  line-height: 1.5;
}

.input.small {
  padding: 4px 8px;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  border: 1px solid #dcdfe4;
  background: var(--ds-color-white-100);
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 13px;
  color: var(--ds-color-gray-50);
  cursor: pointer;
  text-align: left;

  &:hover,
  &:focus-visible {
    border-color: var(--ds-color-primary-100);
    color: var(--ds-color-primary-100);
  }
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.answer {
  display: flex;
  gap: 10px;
  align-items: center;
}

.ai {
  font-size: var(--ds-font-xs);
  color: #7a7d84;
  margin: 0;

  a {
    color: inherit;
    text-decoration: underline;
  }
}

.message {
  font-size: var(--ds-font-sm);
  border-left: 3px solid var(--ds-color-warning-100);
  padding: 4px 0 4px 12px;
  margin: 0;
}

.link {
  align-self: flex-start;
  background: none;
  border: 0;
  padding: 0;
  color: var(--ds-color-primary-100);
  text-decoration: underline;
  cursor: pointer;
  font-size: var(--ds-font-sm);

  &.small {
    font-size: 13px;
    text-decoration: none;
  }
}

.quote {
  font-size: 13.5px;
  color: var(--ds-color-gray-50);
  background: var(--ds-color-gray-10);
  border-radius: 6px;
  padding: 9px 11px;
  margin: 0;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: var(--ds-font-xs);
  color: var(--ds-color-gray-50);
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 5px;
  flex-shrink: 0;

  &.texte {
    background: var(--ds-color-success-100);
  }

  &.hypothese {
    background: var(--ds-color-warning-100);
  }

  &.vous {
    background: var(--ds-color-primary-100);
  }
}

.row {
  display: grid;
  grid-template-columns: minmax(0, 130px) minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 7px 0;
  border-top: 1px solid #f0f1f4;
  font-size: var(--ds-font-sm);

  &:first-child {
    border-top: 0;
  }

  .label {
    color: var(--ds-color-gray-50);
  }

  .value {
    display: flex;
    align-items: center;
    font-weight: var(--ds-weight-medium);
    min-width: 0;
    overflow-wrap: anywhere;
  }
}

.specialist {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  align-items: center;
  justify-content: space-between;
  background: var(--ds-color-primary-25);
  border-top: 1px solid var(--ds-color-primary-50);
  padding: 14px 24px;
  border-radius: 0 0 6px 6px;

  p {
    font-size: var(--ds-font-sm);
    margin: 0;
    flex: 1 1 220px;
  }
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.hp {
  position: absolute;
  left: -9999px;
}

.callback-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(20, 21, 24, 0.45);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 12px 24px;
  overflow-y: auto;
}

.callback-modal {
  background: var(--ds-color-white-100);
  border-radius: 10px;
  width: min(560px, 100%);
  padding: 22px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;

  h2 {
    font-size: var(--ds-font-lg);
    margin: 0;
  }
}

.callback-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.close {
  background: none;
  border: 0;
  font-size: 22px;
  line-height: 1;
  color: var(--ds-color-gray-50);
  cursor: pointer;
}

.callback-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-size: var(--ds-font-sm);

  label {
    display: block;
    font-weight: var(--ds-weight-medium);
    margin-bottom: 5px;
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    @media (max-width: 560px) {
      grid-template-columns: 1fr;
    }
  }

  .optional {
    color: #8a8d93;
    font-weight: var(--ds-weight-regular);
  }

  .needs {
    border: 0;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    legend {
      font-weight: var(--ds-weight-medium);
      margin-bottom: 6px;
    }

    label {
      display: inline-flex;
      gap: 6px;
      align-items: center;
      font-weight: var(--ds-weight-regular);
      border: 1px solid #dcdfe4;
      border-radius: 6px;
      padding: 5px 9px;
      margin: 0;
      cursor: pointer;
    }
  }

  .consent {
    display: flex;
    gap: 9px;
    align-items: flex-start;
    font-weight: var(--ds-weight-regular);

    input {
      margin-top: 3px;
    }
  }

  .error {
    color: var(--ds-color-error-100);
    font-size: var(--ds-font-xs);
  }
}

@media (max-width: 560px) {
  .body {
    padding: 18px 16px;
  }

  .specialist {
    padding: 14px 16px;
  }

  .row {
    grid-template-columns: minmax(0, 1fr) auto;

    .label {
      grid-column: 1 / -1;
      font-size: var(--ds-font-xs);
    }
  }
}
</style>
